import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiActivity, FiAlertTriangle, FiBarChart2, FiCalendar, FiCheckCircle, FiRefreshCw, FiTrendingUp, FiX, FiZap } from 'react-icons/fi';
import './ZapTalkSmartCheckIn.css';
import {
  CheckInRecord,
  SuggestionType,
  WeeklyInsights,
  createCheckIn,
  getCheckIns,
  getWeeklyInsights,
} from '../../../../services/checkInService';

interface CheckInDraft {
  moodScore: number;
  anxietyScore: number;
  energyScore: number;
  emotionTags: string[];
  triggerTags: string[];
  notes: string;
}

interface FeedbackView {
  summary: string;
  suggestion: {
    type: SuggestionType;
    id: string;
    text: string;
  };
}

interface ZapTalkSmartCheckInProps {
  isDarkMode: boolean;
  topic: string;
  asPage?: boolean;
  onBack?: () => void;
}

type RangeDays = 7 | 14 | 30;

const RANGE_OPTIONS: RangeDays[] = [7, 14, 30];
const DRAFT_STORAGE_KEY_PREFIX = 'zaptalk-smart-checkin-draft-v2';
const MAX_HISTORY_FETCH_DAYS = 60;
const DEFAULT_HISTORY_VISIBLE_DATES_PAGE = 8;
const DEFAULT_HISTORY_VISIBLE_DATES_MODAL = 4;
const HISTORY_VISIBLE_STEP = 6;
const CHART_WIDTH = 100;
const CHART_HEIGHT = 56;
const EMOTION_TAGS = ['anxious', 'stressed', 'overwhelmed', 'calm', 'happy', 'sad', 'tired', 'okay'];
const TRIGGER_TAGS = ['work', 'family', 'exam', 'sleep', 'health', 'social', 'future'];

const defaultDraft: CheckInDraft = {
  moodScore: 5,
  anxietyScore: 5,
  energyScore: 5,
  emotionTags: [],
  triggerTags: [],
  notes: '',
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const getLocalDateKey = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const average = (values: number[]) => (values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0);

const buildFeedback = (moodScore: number, anxietyScore: number): FeedbackView => {
  if (anxietyScore >= 8) {
    return { summary: 'High anxiety detected. Let us reduce intensity now.', suggestion: { type: 'grounding', id: 'grounding-2m', text: 'Start 2-minute grounding' } };
  }
  if (moodScore <= 3) {
    return { summary: 'Low mood today. One small step can help.', suggestion: { type: 'microAction', id: 'micro-action', text: 'Take one 5-minute action' } };
  }
  if (moodScore >= 7 && anxietyScore <= 3) {
    return { summary: 'You are doing well. Lock this state with reflection.', suggestion: { type: 'journaling', id: 'gratitude', text: 'Write a gratitude note' } };
  }
  return { summary: 'Check-in saved. Keep steady progress.', suggestion: { type: 'reframe', id: 'reframe', text: 'Try a quick reframe' } };
};

const buildTrendPoints = (values: number[]): string => {
  if (!values.length) return '';
  if (values.length === 1) {
    const y = CHART_HEIGHT - ((values[0] - 1) / 9) * CHART_HEIGHT;
    return `${CHART_WIDTH / 2},${y}`;
  }
  return values
    .map((value, idx) => {
      const x = (idx / (values.length - 1)) * CHART_WIDTH;
      const y = CHART_HEIGHT - ((value - 1) / 9) * CHART_HEIGHT;
      return `${x},${y}`;
    })
    .join(' ');
};

const parseTokenUserId = (): string => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return 'anonymous';
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.userId || payload.id || payload.sub || 'anonymous';
  } catch {
    return 'anonymous';
  }
};

const formatDateForUi = (localDate: string): string => {
  const parsed = new Date(`${localDate}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return localDate;
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const getRelativeDayLabel = (localDate: string): string => {
  const parsed = new Date(`${localDate}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return localDate;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  parsed.setHours(0, 0, 0, 0);
  const diffDays = Math.round((today.getTime() - parsed.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return `${diffDays} days ago`;
};

const GLOBAL_MOOD_TRACKER_TOPIC = 'Mental Health';

const ZapTalkSmartCheckIn: React.FC<ZapTalkSmartCheckInProps> = ({ isDarkMode, topic: _topic, asPage = false, onBack }) => {
  const [isOpen, setIsOpen] = useState(asPage);
  const [draft, setDraft] = useState<CheckInDraft>(defaultDraft);
  const [feedback, setFeedback] = useState<FeedbackView | null>(null);
  const [checkIns, setCheckIns] = useState<CheckInRecord[]>([]);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [rangeDays, setRangeDays] = useState<RangeDays>(14);
  const [insights, setInsights] = useState<WeeklyInsights | null>(null);
  const [insightsError, setInsightsError] = useState<string | null>(null);
  const [isInsightsLoading, setIsInsightsLoading] = useState(false);
  const initialHistoryVisibleDates = asPage ? DEFAULT_HISTORY_VISIBLE_DATES_PAGE : DEFAULT_HISTORY_VISIBLE_DATES_MODAL;
  const [historyVisibleDates, setHistoryVisibleDates] = useState(initialHistoryVisibleDates);

  const userId = useMemo(() => parseTokenUserId(), []);
  const normalizedTopic = useMemo(() => GLOBAL_MOOD_TRACKER_TOPIC, []);
  const draftStorageKey = useMemo(() => `${DRAFT_STORAGE_KEY_PREFIX}:${userId}:${normalizedTopic.toLowerCase()}`, [userId, normalizedTopic]);

  const refreshCheckIns = useCallback(async () => {
    setIsHistoryLoading(true);
    setHistoryError(null);
    try {
      const endDate = new Date();
      endDate.setHours(0, 0, 0, 0);
      const startDate = new Date(endDate);
      startDate.setDate(startDate.getDate() - (MAX_HISTORY_FETCH_DAYS - 1));
      const records = await getCheckIns({
        topic: normalizedTopic,
        from: getLocalDateKey(startDate),
        to: getLocalDateKey(endDate),
        limit: 200,
        page: 1,
      });
      setCheckIns(records);
    } catch (error) {
      setHistoryError(error instanceof Error ? error.message : 'Failed to load mood history.');
    } finally {
      setIsHistoryLoading(false);
    }
  }, [normalizedTopic]);

  const refreshInsights = useCallback(async () => {
    setIsInsightsLoading(true);
    setInsightsError(null);
    try {
      const data = await getWeeklyInsights(getLocalDateKey(new Date()), normalizedTopic);
      setInsights(data);
    } catch (error) {
      setInsightsError(error instanceof Error ? error.message : 'Failed to load insights.');
    } finally {
      setIsInsightsLoading(false);
    }
  }, [normalizedTopic]);

  const refreshAll = useCallback(async () => {
    await Promise.allSettled([refreshCheckIns(), refreshInsights()]);
  }, [refreshCheckIns, refreshInsights]);

  useEffect(() => {
    void refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    if (asPage && !isOpen) {
      setIsOpen(true);
    }
  }, [asPage, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const raw = localStorage.getItem(draftStorageKey);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw) as Partial<CheckInDraft>;
      setDraft({
        moodScore: clamp(Number(parsed.moodScore || 5), 1, 10),
        anxietyScore: clamp(Number(parsed.anxietyScore || 5), 1, 10),
        energyScore: clamp(Number(parsed.energyScore || 5), 1, 10),
        emotionTags: Array.isArray(parsed.emotionTags) ? parsed.emotionTags.slice(0, 5) : [],
        triggerTags: Array.isArray(parsed.triggerTags) ? parsed.triggerTags.slice(0, 5) : [],
        notes: typeof parsed.notes === 'string' ? parsed.notes.slice(0, 500) : '',
      });
    } catch {}
  }, [draftStorageKey, isOpen]);

  useEffect(() => {
    if (isOpen) localStorage.setItem(draftStorageKey, JSON.stringify(draft));
  }, [draft, draftStorageKey, isOpen]);

  useEffect(() => {
    setHistoryVisibleDates(initialHistoryVisibleDates);
  }, [initialHistoryVisibleDates, normalizedTopic, rangeDays]);

  const streak = useMemo(() => {
    const unique = new Set(checkIns.map((x) => x.localDate));
    let s = 0;
    const d = new Date();
    while (unique.has(getLocalDateKey(d))) {
      s += 1;
      d.setDate(d.getDate() - 1);
    }
    return s;
  }, [checkIns]);

  const filtered = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - (rangeDays - 1));
    return checkIns.filter((x) => new Date(`${x.localDate}T00:00:00`) >= start);
  }, [checkIns, rangeDays]);

  const trendAsc = useMemo(
    () => [...filtered].sort((a, b) => a.localDate.localeCompare(b.localDate) || new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()),
    [filtered],
  );
  const moodTrend = buildTrendPoints(trendAsc.map((x) => x.moodScore));
  const anxietyTrend = buildTrendPoints(trendAsc.map((x) => x.anxietyScore));

  const avgMood = average(filtered.map((x) => x.moodScore));
  const avgAnxiety = average(filtered.map((x) => x.anxietyScore));
  const avgEnergy = average(filtered.map((x) => x.energyScore).filter((v): v is number => typeof v === 'number'));
  const completionRate = Math.round((new Set(filtered.map((x) => x.localDate)).size / rangeDays) * 100);
  const historyByDate = useMemo(() => {
    const grouped = new Map<string, CheckInRecord[]>();
    const sortedDesc = [...filtered].sort((a, b) => b.localDate.localeCompare(a.localDate) || new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    sortedDesc.forEach((entry) => {
      const bucket = grouped.get(entry.localDate);
      if (bucket) {
        bucket.push(entry);
      } else {
        grouped.set(entry.localDate, [entry]);
      }
    });

    return Array.from(grouped.entries()).map(([localDate, entries]) => ({
      localDate,
      latest: entries[0],
      count: entries.length,
    }));
  }, [filtered]);
  const visibleHistory = useMemo(() => historyByDate.slice(0, historyVisibleDates), [historyByDate, historyVisibleDates]);
  const hasMoreHistory = visibleHistory.length < historyByDate.length;
  const canCollapseHistory = visibleHistory.length > initialHistoryVisibleDates;
  const dominantEmotion = useMemo(() => {
    const counts = new Map<string, number>();
    filtered.forEach((entry) => {
      entry.emotionTags.forEach((tag) => counts.set(tag, (counts.get(tag) ?? 0) + 1));
    });
    const top = [...counts.entries()].sort((a, b) => b[1] - a[1])[0];
    return top ? top[0] : 'none yet';
  }, [filtered]);

  const toggleTag = (key: 'emotionTags' | 'triggerTags', tag: string) => {
    setDraft((prev) => {
      const current = prev[key];
      if (current.includes(tag)) return { ...prev, [key]: current.filter((x) => x !== tag) };
      if (current.length >= 5) return prev;
      return { ...prev, [key]: [...current, tag] };
    });
  };

  const handleSave = async () => {
    if (isSaving) return;
    setSaveError(null);
    setIsSaving(true);
    const feedbackPayload = buildFeedback(draft.moodScore, draft.anxietyScore);
    try {
      const result = await createCheckIn({
        topic: normalizedTopic,
        localDate: getLocalDateKey(new Date()),
        moodScore: draft.moodScore,
        anxietyScore: draft.anxietyScore,
        energyScore: draft.energyScore,
        emotionTags: draft.emotionTags.slice(0, 5),
        triggerTags: draft.triggerTags.slice(0, 5),
        notes: draft.notes.trim().slice(0, 500),
        context: {},
      });
      setFeedback(result.feedback ?? feedbackPayload);
      setCheckIns((prev) => [result.checkIn, ...prev.filter((x) => x._id !== result.checkIn._id)]);
      setDraft(defaultDraft);
      localStorage.removeItem(draftStorageKey);
      void refreshAll();
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to save check-in.');
    } finally {
      setIsSaving(false);
    }
  };

  const themeClass = isDarkMode ? 'dark' : 'light';
  const todayDateLabel = formatDateForUi(getLocalDateKey(new Date()));

  return (
    <>
      {!asPage && (
        <button type="button" onClick={() => setIsOpen(true)} className={`voice-toggle-btn zaptalk-tracker-launcher ${themeClass}`}>
          <FiActivity size={18} />
          Mood Tracker
          <span className="zaptalk-tracker-launcher__streak">{streak}d</span>
        </button>
      )}

      {(asPage || isOpen) && (
        <div className={`zaptalk-tracker-overlay${asPage ? ' as-page' : ''}`} onClick={() => (!asPage ? setIsOpen(false) : undefined)}>
          <div
            className={`zaptalk-tracker-shell${asPage ? ' as-page' : ''} ${themeClass}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="zaptalk-tracker-header">
              <div className="zaptalk-tracker-title-block">
                <div className="zaptalk-tracker-title">Advanced Mood Tracker</div>
              </div>
              <div className="zaptalk-tracker-header-actions">
                <span className="zaptalk-tracker-date-pill">{todayDateLabel}</span>
                {asPage ? (
                  onBack ? (
                    <button type="button" onClick={() => onBack()} className="zaptalk-btn zaptalk-btn--ghost">
                      Back to chat
                    </button>
                  ) : null
                ) : (
                  <button type="button" onClick={() => setIsOpen(false)} className="zaptalk-icon-btn" aria-label="Close mood tracker">
                    <FiX />
                  </button>
                )}
              </div>
            </div>

            <div className="zaptalk-tracker-kpi-grid">
              <div className="zaptalk-kpi-card">
                <span className="zaptalk-kpi-icon zaptalk-kpi-icon--mood"><FiActivity size={14} /></span>
                <div>
                  <div className="zaptalk-kpi-label">Avg Mood</div>
                  <div className="zaptalk-kpi-value">{filtered.length ? avgMood.toFixed(1) : '--'}</div>
                </div>
              </div>
              <div className="zaptalk-kpi-card">
                <span className="zaptalk-kpi-icon zaptalk-kpi-icon--anxiety"><FiAlertTriangle size={14} /></span>
                <div>
                  <div className="zaptalk-kpi-label">Avg Anxiety</div>
                  <div className="zaptalk-kpi-value">{filtered.length ? avgAnxiety.toFixed(1) : '--'}</div>
                </div>
              </div>
              <div className="zaptalk-kpi-card">
                <span className="zaptalk-kpi-icon zaptalk-kpi-icon--energy"><FiZap size={14} /></span>
                <div>
                  <div className="zaptalk-kpi-label">Avg Energy</div>
                  <div className="zaptalk-kpi-value">{filtered.length ? avgEnergy.toFixed(1) : '--'}</div>
                </div>
              </div>
              <div className="zaptalk-kpi-card">
                <span className="zaptalk-kpi-icon zaptalk-kpi-icon--completion"><FiCheckCircle size={14} /></span>
                <div>
                  <div className="zaptalk-kpi-label">Completion</div>
                  <div className="zaptalk-kpi-value">{completionRate}%</div>
                </div>
              </div>
            </div>

            <div className={`zaptalk-tracker-main-grid${asPage ? '' : ' is-modal'}`}>
              <div className="zaptalk-tracker-main-col zaptalk-tracker-analytics-col">
                <div className="zaptalk-tracker-panel zaptalk-tracker-panel-trend">
                  <div className="zaptalk-panel-head">
                    <div className="zaptalk-panel-title"><FiBarChart2 /> Trend</div>
                    <div className="zaptalk-chip-group">
                      {RANGE_OPTIONS.map((d) => (
                        <button key={d} type="button" onClick={() => setRangeDays(d)} className={`zaptalk-chip-btn${d === rangeDays ? ' is-active' : ''}`}>
                          {d}d
                        </button>
                      ))}
                    </div>
                  </div>
                  {trendAsc.length ? (
                    <>
                      <svg viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`} width="100%" height={asPage ? 190 : 150} className="zaptalk-trend-chart">
                        <line x1={0} y1={CHART_HEIGHT} x2={CHART_WIDTH} y2={CHART_HEIGHT} className="zaptalk-trend-axis" strokeWidth={0.6} />
                        {!!moodTrend && <polyline fill="none" className="zaptalk-trend-line mood" strokeWidth={2} points={moodTrend} />}
                        {!!anxietyTrend && <polyline fill="none" className="zaptalk-trend-line anxiety" strokeWidth={2} points={anxietyTrend} />}
                      </svg>
                      <div className="zaptalk-helper-text">Mood (teal) | Anxiety (amber)</div>
                    </>
                  ) : (
                    <div className="zaptalk-empty-text">No trend data in this range.</div>
                  )}
                </div>

                <div className="zaptalk-tracker-panel zaptalk-tracker-panel-insights">
                  <div className="zaptalk-panel-head">
                    <div className="zaptalk-panel-title"><FiTrendingUp /> Weekly smart insights</div>
                    <button type="button" onClick={() => void refreshAll()} className="zaptalk-btn zaptalk-btn--ghost zaptalk-btn--icon">
                      <FiRefreshCw size={12} />
                      Refresh
                    </button>
                  </div>
                  {isInsightsLoading && <div className="zaptalk-empty-text">Generating insights...</div>}
                  {!isInsightsLoading && insightsError && <div className="zaptalk-warning-text">{insightsError}</div>}
                  {!isInsightsLoading && !insightsError && insights && (
                    <div className="zaptalk-insights-content">
                      <div className="zaptalk-helper-text zaptalk-inline-icon"><FiCalendar /> {insights.from} to {insights.to}</div>
                      <div className="zaptalk-insight-line">{insights.weekdayPattern}</div>
                      <div className="zaptalk-insight-line">{insights.sleepCorrelation}</div>
                    </div>
                  )}
                </div>
              </div>

              <div className="zaptalk-tracker-panel zaptalk-tracker-panel-checkin">
                <div className="zaptalk-panel-title">Quick check-in</div>
                <div className="zaptalk-checkin-presets">
                  <button type="button" onClick={() => setDraft((p) => ({ ...p, moodScore: 8, anxietyScore: 2, energyScore: 7 }))} className="zaptalk-action-chip">Calm day</button>
                  <button type="button" onClick={() => setDraft((p) => ({ ...p, moodScore: 4, anxietyScore: 8, energyScore: 4 }))} className="zaptalk-action-chip">Stress spike</button>
                  <button type="button" onClick={() => setDraft((p) => ({ ...p, moodScore: 3, anxietyScore: 5, energyScore: 3 }))} className="zaptalk-action-chip">Low mood</button>
                </div>

                <div className="zaptalk-slider-row">
                  <div className="zaptalk-slider-head"><span>Mood</span><strong>{draft.moodScore}/10</strong></div>
                  <input type="range" min={1} max={10} value={draft.moodScore} onChange={(e) => setDraft((p) => ({ ...p, moodScore: clamp(Number(e.target.value), 1, 10) }))} className="zaptalk-slider zaptalk-slider--mood" />
                </div>
                <div className="zaptalk-slider-row">
                  <div className="zaptalk-slider-head"><span>Anxiety</span><strong>{draft.anxietyScore}/10</strong></div>
                  <input type="range" min={1} max={10} value={draft.anxietyScore} onChange={(e) => setDraft((p) => ({ ...p, anxietyScore: clamp(Number(e.target.value), 1, 10) }))} className="zaptalk-slider zaptalk-slider--anxiety" />
                </div>
                <div className="zaptalk-slider-row">
                  <div className="zaptalk-slider-head"><span>Energy</span><strong>{draft.energyScore}/10</strong></div>
                  <input type="range" min={1} max={10} value={draft.energyScore} onChange={(e) => setDraft((p) => ({ ...p, energyScore: clamp(Number(e.target.value), 1, 10) }))} className="zaptalk-slider zaptalk-slider--energy" />
                </div>

                <div className="zaptalk-label">Emotion tags</div>
                <div className="zaptalk-tag-grid">
                  {EMOTION_TAGS.map((tag) => {
                    const selected = draft.emotionTags.includes(tag);
                    return (
                      <button key={tag} type="button" onClick={() => toggleTag('emotionTags', tag)} className={`zaptalk-tag-chip emotion${selected ? ' is-selected' : ''}`}>
                        {tag}
                      </button>
                    );
                  })}
                </div>

                <div className="zaptalk-label">Trigger tags</div>
                <div className="zaptalk-tag-grid">
                  {TRIGGER_TAGS.map((tag) => {
                    const selected = draft.triggerTags.includes(tag);
                    return (
                      <button key={tag} type="button" onClick={() => toggleTag('triggerTags', tag)} className={`zaptalk-tag-chip trigger${selected ? ' is-selected' : ''}`}>
                        {tag}
                      </button>
                    );
                  })}
                </div>

                <textarea value={draft.notes} onChange={(e) => setDraft((p) => ({ ...p, notes: e.target.value.slice(0, 500) }))} placeholder="Short note (optional)" className="zaptalk-notes-input" />
                {!!saveError && <div className="zaptalk-error-text">{saveError}</div>}
                <button type="button" onClick={() => void handleSave()} disabled={isSaving} className="zaptalk-btn zaptalk-btn--primary zaptalk-save-btn">
                  {isSaving ? 'Saving...' : 'Save check in'}
                </button>
              </div>
            </div>

            <div className="zaptalk-tracker-history">
              <div className="zaptalk-history-title">Daily mood list</div>
              <div className="zaptalk-helper-text">
                Dominant emotion ({rangeDays}d): {dominantEmotion}
                {!isHistoryLoading && !historyError && historyByDate.length > 0 ? ` | Showing ${visibleHistory.length} of ${historyByDate.length} days` : ''}
              </div>
              {isHistoryLoading && <div className="zaptalk-empty-text">Loading mood history...</div>}
              {!isHistoryLoading && historyError && <div className="zaptalk-warning-text">{historyError}</div>}
              {!isHistoryLoading && !historyError && !historyByDate.length && <div className="zaptalk-empty-text">No check-ins in this range.</div>}
              {!isHistoryLoading && !historyError && (
                <div className="zaptalk-tracker-history-list">
                  {visibleHistory.map((item) => (
                    <div key={`${item.localDate}-${item.latest._id}`} className="zaptalk-tracker-history-card">
                      <div className="zaptalk-history-card-head">
                        <strong>{formatDateForUi(item.localDate)}</strong>
                        <span className="zaptalk-helper-text">{getRelativeDayLabel(item.localDate)}</span>
                      </div>
                      <div className="zaptalk-history-score-line">
                        Mood {item.latest.moodScore}/10 | Anxiety {item.latest.anxietyScore}/10 | Energy {item.latest.energyScore || '--'}/10
                      </div>
                      {item.count > 1 && <div className="zaptalk-helper-text">{item.count} check-ins on this day (showing latest)</div>}
                      <div className="zaptalk-history-tags">
                        {item.latest.emotionTags.slice(0, 5).map((tag) => <span key={`${item.latest._id}-e-${tag}`} className="zaptalk-history-tag">{tag}</span>)}
                        {item.latest.triggerTags.slice(0, 5).map((tag) => <span key={`${item.latest._id}-t-${tag}`} className="zaptalk-history-tag trigger">{tag}</span>)}
                      </div>
                      {!!item.latest.notes && <div className="zaptalk-history-note">{item.latest.notes}</div>}
                    </div>
                  ))}
                </div>
              )}
              {!isHistoryLoading && !historyError && historyByDate.length > 0 && (
                <div className="zaptalk-history-actions">
                  {hasMoreHistory && (
                    <button
                      type="button"
                      onClick={() => setHistoryVisibleDates((prev) => Math.min(prev + HISTORY_VISIBLE_STEP, historyByDate.length))}
                      className="zaptalk-btn zaptalk-btn--ghost"
                    >
                      Load older dates
                    </button>
                  )}
                  {canCollapseHistory && (
                    <button
                      type="button"
                      onClick={() => setHistoryVisibleDates(initialHistoryVisibleDates)}
                      className="zaptalk-btn zaptalk-btn--ghost"
                    >
                      Show less
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {feedback && (
        <div className="zaptalk-feedback-overlay" onClick={() => setFeedback(null)}>
          <div className={`zaptalk-feedback-card ${themeClass}`} onClick={(e) => e.stopPropagation()}>
            <div className="zaptalk-feedback-head">
              <strong>Smart feedback</strong>
              <button type="button" onClick={() => setFeedback(null)} className="zaptalk-icon-btn" aria-label="Close feedback">
                <FiX />
              </button>
            </div>
            <p className="zaptalk-feedback-summary">{feedback.summary}</p>
            <button type="button" onClick={() => setFeedback(null)} className="zaptalk-btn zaptalk-btn--primary">{feedback.suggestion.text}</button>
          </div>
        </div>
      )}
    </>
  );
};

export default ZapTalkSmartCheckIn;

